import {
  Box,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import {useEffect, useMemo, useState} from 'react';
import dayjs, {Dayjs} from 'dayjs';
import {useSelector} from 'react-redux';
import {apiDelete, apiPost} from '../../../axiosConfig';
import {ContentCopy, Edit, Info, NotInterested} from '@mui/icons-material';
import {useQueryClient} from '@tanstack/react-query';
import {companyIdSelector} from '../../../store/Slices/companySlice';
import IconViewButton from '../../../components/Button/IconViewButton';
import {
  getTimedMsgOnDateApiPath,
  useTimedMessageOnDate,
} from '../../../react_query/messages/timed-messages/TimedMsgOnDate';
import MusicPreviewBtn from '../../../components/music/MusicPreviewBtn';
import {
  ITimedMessageDetailedResponse,
  ITimedSequenceType,
} from '../../../react_query/messages/timed-messages/ViewTimedMsgDetails';
import {TimedSequenceToMessageFormat} from './TimedMsgDetails/TimedMsgFunctions';
import DownloadButton from '../../../components/Button/DownloadButton';
import TimedMessageCreate from './TimedMessageCreate';
import {UserCanAny} from '../../../components/UserCan';
import {TimedMsgDeActivate} from './TimedMsgRestoreDeActive';
import {IOSSwitch} from '../../../components/styles/ToggleButton';
import {ITimedRequestDateRange} from '../../../dto/timed-message/TimedRequestDateRange.dto';

const TimedMessageListOfDate = ({
  search,
  setViewId,
  selectedIds,
  selectedDate,
  setStatus,
}: {
  search: string;
  setViewId: (val: number) => void;
  selectedIds: number[];
  selectedDate: Dayjs | null;
  setStatus: React.Dispatch<React.SetStateAction<string>>;
}) => {
  const companyId = useSelector(companyIdSelector);
  const [dialogType, setDialogType] = useState('');
  const [editId, setEditId] = useState<number | null>(null);
  const [rowToggle, setRowToggle] = useState<Record<string, boolean>>({});

  const {data, isFetching, isError} = useTimedMessageOnDate(companyId, {
    timedMessageIds: selectedIds,
    date: selectedDate?.format('YYYY-MM-DD') ?? '',
  });
  const queryClient = useQueryClient();

  const listMessages = useMemo(() => {
    if (data && !isFetching && !isError) {
      const newData: (ITimedMessageDetailedResponse & {time: string})[] = [];
      data.forEach(val => {
        if (!search || val.name.toLowerCase().includes(search.toLowerCase())) {
          val.day_times.forEach(dayTimes => {
            dayTimes.start_times.forEach(time => {
              newData.push({...val, time});
            });
          });
        }
      });
      return newData.sort((a, b) => a.time.localeCompare(b.time));
    }
    return [];
  }, [data, isError, isFetching, search]);

  const isFutureDate = (selectedDate: Dayjs | null) => {
    if (!selectedDate) return false;
    return selectedDate.isAfter(dayjs(), 'day');
  };

  useEffect(() => {
    if (!data || listMessages.length === 0) {
      setRowToggle({});
      return;
    }

    const newToggle: Record<string, boolean> = {};

    data.forEach(row => {
      row.day_times.forEach(dayTimes => {
        dayTimes.start_times.forEach(time => {
          const key = `${row.id}_${time}`;
          const isExcluded = row.date_ranges?.some(
            dr =>
              dr.excluded_dates?.some(
                ed =>
                  ed.excluded_date === selectedDate?.format('YYYY-MM-DD') &&
                  (!ed.exclude_times || ed.exclude_times.includes(time)),
              ),
          );
          newToggle[key] = !!isExcluded;
        });
      });
    });

    setRowToggle(newToggle);
  }, [data, listMessages, selectedDate]);

  const getDateRangeId = (
    dateRanges: ITimedRequestDateRange[],
    selectedDate: string,
  ): number | undefined => {
    const selDate = new Date(selectedDate); // convert string to Date
    for (const range of dateRanges) {
      const start = new Date(range.start_date);
      const end = new Date(range.end_date);
      if (selDate >= start && selDate <= end) {
        return range.id;
      }
    }
    return undefined;
  };

  const handleDoNotPlay = async (
    timedMsgIds: number | number[],
    date: string,
    dateRangeId: number,
    time: string,
  ) => {
    const payloads = {
      timed_request_id: timedMsgIds,
      trdr_id: dateRangeId,
      excluded_date: date,
      exclude_times: time,
    };

    await apiPost(
      `/v1/company/${companyId}/message/timed-message/excluded-dates`,
      payloads,
    );
  };

  const handleDeleteExcludedDate = async (
    timedMsgId: number,
    dateRangeId: number,
    excludedDate: string,
    time: string,
  ) => {
    await apiDelete(
      `/v1/company/${companyId}/message/timed-message/excluded-dates/${dateRangeId}`,
      {
        data: {
          excluded_date: excludedDate,
          exclude_times: time,
          timed_request_id: timedMsgId,
        },
      },
    );
  };

  const handleRowToggle = async (
    row: ITimedMessageDetailedResponse,
    time: string,
    checked: boolean,
  ) => {
    const date = selectedDate?.format('YYYY-MM-DD') ?? '';
    const dateRangeId = getDateRangeId(row.date_ranges || [], date);

    if (!dateRangeId) return;
    const key = `${row.id}_${time}`;
    setRowToggle(prev => ({...prev, [key]: checked}));

    try {
      if (!checked) {
        // Add exclusion
        await handleDoNotPlay(row.id, date, dateRangeId, row.time ?? '');
      } else {
        // Remove exclusion
        await handleDeleteExcludedDate(
          row.id,
          dateRangeId,
          date,
          row.time ?? '',
        );
      }

      queryClient.invalidateQueries({
        queryKey: [
          getTimedMsgOnDateApiPath(companyId),
          {date: selectedDate?.format('YYYY-MM-DD')},
        ],
        exact: false,
        refetchType: 'active',
      });
    } catch (error) {
      /* eslint-disable-next-line no-console */
      console.error(error);
    }
  };

  const handleSelectAll = async (checked: boolean) => {
    const date = selectedDate?.format('YYYY-MM-DD') ?? '';

    // Update toggle state for all row-times (store inverted for UI)
    const newToggleState: Record<string, boolean> = {};
    listMessages.forEach(row => {
      const key = `${row.id}_${row.time}`;
      newToggleState[key] = !checked;
    });
    setRowToggle(newToggleState);

    if (!checked) {
      // If toggle is OFF (red), add exclusions for all row-times
      await Promise.all(
        listMessages.map(row => {
          const dateRangeId = getDateRangeId(row.date_ranges || [], date);
          if (!dateRangeId) return Promise.resolve();

          const dateRange = row.date_ranges?.find(dr => dr.id === dateRangeId);
          const alreadyExcluded = dateRange?.excluded_dates?.some(
            ed =>
              ed.excluded_date === date &&
              (!ed.exclude_times || ed.exclude_times.includes(row.time)),
          );

          if (alreadyExcluded) return Promise.resolve();

          return handleDoNotPlay(row.id, date, dateRangeId, row.time);
        }),
      );
    } else {
      // If toggle is ON (green), remove all exclusions
      const exclusions = listMessages.flatMap(
        row =>
          row.date_ranges?.flatMap(dr =>
            (dr.excluded_dates || []).filter(
              ed =>
                ed.excluded_date === date &&
                (!ed.exclude_times || ed.exclude_times.includes(row.time)),
            ),
          ) || [],
      );

      const uniqueExclusions = Array.from(
        new Map(
          exclusions.map(ed => [
            `${ed.trdr_id}_${ed.excluded_date}_${ed.exclude_times}`,
            ed,
          ]),
        ).values(),
      );

      await Promise.all(
        uniqueExclusions.map(exclusion =>
          handleDeleteExcludedDate(
            exclusion.timed_request_id,
            exclusion.trdr_id,
            date,
            exclusion.exclude_times ?? '',
          ),
        ),
      );
    }

    // Invalidate cache after bulk operation
    queryClient.invalidateQueries({
      queryKey: [getTimedMsgOnDateApiPath(companyId)],
      exact: false,
      refetchType: 'active',
    });
  };

  const playableRows = listMessages.filter(row => row.status !== 'In Progress');
  const allSelected =
    playableRows.length > 0 &&
    playableRows.some(row => {
      const key = `${row.id}_${row.time}`;
      return !rowToggle[key];
    });

  return (
    <TableContainer className='w-full h-full max-h-550 overflow-auto scrollbar'>
      <Table aria-label='timed-by-date table'>
        <TableHead className='blue-variant29'>
          <TableRow>
            <TableCell>Message Name</TableCell>
            <TableCell>Play Times</TableCell>
            <TableCell>Preview</TableCell>
            <TableCell align='center' colSpan={4}>
              Action
            </TableCell>
            <TableCell align='center'>
              {playableRows.length > 0 &&
                (() => {
                  const switchElement = (
                    <Tooltip
                      title={allSelected ? 'Play' : "Don't Play"}
                      describeChild>
                      <Box display='flex' justifyContent='center'>
                        <IOSSwitch
                          checked={allSelected}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleSelectAll(e.target.checked)
                          }
                          inputProps={{
                            'aria-label':
                              'select all timed messages for the date',
                          }}
                        />
                      </Box>
                    </Tooltip>
                  );

                  return isFutureDate(selectedDate) ? (
                    switchElement
                  ) : (
                    <UserCanAny scope='is-admin,staff'>
                      {switchElement}
                    </UserCanAny>
                  );
                })()}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isFetching ? (
            <TableRow>
              <TableCell colSpan={8}>
                <LinearProgress />
              </TableCell>
            </TableRow>
          ) : listMessages.length ? (
            <>
              {listMessages.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>
                    {dayjs(row.time, 'HH:mm:ss').format('hh:mm A')}
                  </TableCell>
                  <TableCell>
                    {row.timed_sequences ? (
                      <TimedMusicPreview
                        sequence={row.timed_sequences}
                        index={index}
                      />
                    ) : null}
                  </TableCell>
                  <TableCell align='center'>
                    <IconViewButton onClick={() => setViewId(row.id)} />
                  </TableCell>
                  {row.status !== 'In Progress' ? (
                    <UserCanAny scope='is-admin,partner,timed-message:create'>
                      <>
                        <TableCell>
                          <IconButton
                            aria-label='edit'
                            color='primary'
                            title='Edit'
                            onClick={() => {
                              setDialogType('update');
                              setEditId(row.id);
                            }}>
                            <Edit />
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <IconButton
                            onClick={() => {
                              setDialogType('duplicate');
                              setEditId(row.id);
                            }}
                            title='Duplicate'>
                            <ContentCopy />
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <IconButton
                            aria-label='close'
                            color='error'
                            title='Deactivate'
                            onClick={() => {
                              setDialogType('de-activate');
                              setEditId(row.id);
                            }}>
                            <NotInterested />
                          </IconButton>
                        </TableCell>
                        <TableCell align='center'>
                          <Box>
                            {(() => {
                              const key = `${row.id}_${row.time}`;

                              const isRowExcluded =
                                row.date_ranges?.some(
                                  dr =>
                                    dr.excluded_dates?.some(
                                      ed =>
                                        ed.excluded_date ===
                                          selectedDate?.format('YYYY-MM-DD') &&
                                        (!ed.exclude_times ||
                                          ed.exclude_times.includes(row.time)),
                                    ),
                                ) ?? false;

                              const isPlaying = rowToggle.hasOwnProperty(key)
                                ? !rowToggle[key]
                                : !isRowExcluded;

                              return (() => {
                                const isAllowed = isFutureDate(selectedDate);
                                const rowSwitchElement = (
                                  <IOSSwitch
                                    checked={isPlaying}
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>,
                                    ) =>
                                      handleRowToggle(
                                        row,
                                        row.time,
                                        e.target.checked,
                                      )
                                    }
                                    inputProps={{
                                      'aria-label': `toggle for ${row.name}}`,
                                    }}
                                  />
                                );
                                return (
                                  <Tooltip
                                    title={isPlaying ? 'Play' : "Don't Play"}
                                    arrow
                                    describeChild>
                                    <span className='cursor-pointer opacity-100'>
                                      {isAllowed ? (
                                        rowSwitchElement
                                      ) : (
                                        <UserCanAny scope='is-admin,staff'>
                                          {rowSwitchElement}
                                        </UserCanAny>
                                      )}
                                    </span>
                                  </Tooltip>
                                );
                              })();
                            })()}
                          </Box>
                        </TableCell>
                      </>
                    </UserCanAny>
                  ) : null}
                </TableRow>
              ))}
            </>
          ) : (
            <TableRow>
              <TableCell colSpan={8}>No messages found</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {dialogType !== '' && dialogType !== 'de-activate' && editId !== null && (
        <TimedMessageCreate
          onClose={() => {
            setDialogType('');
            setEditId(null);
          }}
          timedId={editId}
          type={dialogType}
          setStatus={setStatus}
        />
      )}
      {dialogType === 'de-activate' ? (
        <TimedMsgDeActivate
          request_id={Number(editId)}
          handleCancel={() => {
            setDialogType('');
            queryClient.invalidateQueries({
              queryKey: [getTimedMsgOnDateApiPath(companyId)],
              exact: false,
              refetchType: 'active',
            });
          }}
        />
      ) : null}
    </TableContainer>
  );
};

export default TimedMessageListOfDate;

const TimedMusicPreview = ({
  sequence,
  index,
}: {
  sequence: ITimedSequenceType;
  index: number;
}) => {
  const formattedSequence = TimedSequenceToMessageFormat(sequence);
  if (formattedSequence[0]?.track?.url && formattedSequence[0]?.track?.id) {
    return (
      <Stack direction='row' spacing={2}>
        <MusicPreviewBtn
          id={index}
          url={formattedSequence[0].track.url}
          track_name={formattedSequence[0].track.track_name}
          track_artist={formattedSequence[0].track.track_artist}
        />
        <DownloadButton
          filename={formattedSequence[0].track.track_name}
          url={formattedSequence[0].track.url}
        />
      </Stack>
    );
  }

  return (
    <IconButton title='In-progress'>
      <Info />
    </IconButton>
  );
};
